import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Play, Sparkles } from "lucide-react";
import * as Lucide from "lucide-react";
import { useContent } from "@/lib/cms";
import { WatchDemoModal } from "@/components/landing/WatchDemoModal";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const Icon = ({ name, className }) => {
  const Cmp = Lucide[name] || Lucide.Sparkles;
  return <Cmp className={className} />;
};

/* ── Chat demo animation (now CMS-driven) ────────────────────────── */

const Bubble = ({ side, children }) => (
  <div className={`flex ${side === "out" ? "justify-end" : "justify-start"}`}>
    <div
      className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-snug shadow-sm ${
        side === "out"
          ? "bg-emerald-500 text-white rounded-br-md"
          : "bg-white text-slate-800 rounded-bl-md border border-slate-100"
      }`}
    >
      {children}
    </div>
  </div>
);

const Typing = () => (
  <div className="flex justify-start">
    <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-md px-3 py-2 flex items-center gap-1">
      <span className="typing-dot block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="typing-dot block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="typing-dot block h-1.5 w-1.5 rounded-full bg-slate-400" />
    </div>
  </div>
);

const ChatDemo = ({ config }) => {
  const conversation = useMemo(() => config?.conversation || [], [config]);
  const [shown, setShown] = useState([]);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      while (!cancelled) {
        setShown([]);
        for (let i = 0; i < conversation.length; i++) {
          if (cancelled) return;
          setTyping(true);
          await new Promise((r) => setTimeout(r, conversation[i].delay_ms ?? 700));
          if (cancelled) return;
          setTyping(false);
          setShown((s) => [...s, conversation[i]]);
          await new Promise((r) => setTimeout(r, 700));
        }
        await new Promise((r) => setTimeout(r, 2400));
      }
    };
    if (conversation.length) run();
    return () => { cancelled = true; };
  }, [conversation]);

  const assistantName = config?.assistant_name || "Wappit Assistant";
  const statusText = config?.status_text || "online";
  const inputPlaceholder = config?.input_placeholder || "Type a message…";

  return (
    <div data-testid="hero-chat-demo" className="w-[320px] sm:w-[360px] rounded-[28px] bg-[#0f172a] p-3 shadow-2xl">
      <div className="rounded-[20px] overflow-hidden bg-[#e8f1ec]">
        <div className="bg-emerald-600 text-white px-4 py-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">W</div>
          <div>
            <div className="text-sm font-semibold">{assistantName}</div>
            <div className="text-[11px] opacity-80 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" /> {statusText}
            </div>
          </div>
        </div>
        <div
          className="px-3 py-4 space-y-2 min-h-[320px]"
          style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22><circle cx=%221%22 cy=%221%22 r=%221%22 fill=%22%23cfd9d3%22/></svg>')" }}
        >
          {shown.map((m, i) => (
            <Bubble key={i} side={m.side}>{m.text}</Bubble>
          ))}
          {typing && shown.length < conversation.length && <Typing />}
        </div>
        <div className="px-3 py-2 bg-white border-t border-slate-100 flex items-center gap-2">
          <div className="flex-1 h-9 rounded-full bg-slate-100 px-3 flex items-center text-xs text-slate-400">
            {inputPlaceholder}
          </div>
          <div className="h-9 w-9 rounded-full bg-emerald-500 flex items-center justify-center">
            <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
          </div>
        </div>
      </div>
    </div>
  );
};

const POSITION_CLASS = {
  "top-left":      "-left-16 top-10",
  "top-right":     "-right-8 top-10",
  "bottom-left":   "-left-16 bottom-10",
  "bottom-right":  "-right-8 bottom-10",
};

const FloatingStat = ({ position, label, value, delta }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.6, duration: 0.6 }}
    className={`absolute ${POSITION_CLASS[position] || "-right-8 bottom-10"} glass rounded-2xl px-4 py-3 shadow-xl hidden sm:block`}
  >
    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
    <div className="flex items-end gap-2">
      <div className="text-xl font-extrabold text-slate-900">{value}</div>
      {delta && <div className="text-[11px] font-semibold text-emerald-600">{delta}</div>}
    </div>
  </motion.div>
);

/* ── Hero ────────────────────────────────────────────────────────── */

export const Hero = ({ onBookDemo }) => {
  const { data: cms } = useContent("hero", {});

  const headlinePre = cms.headline_pre || "Convert chats into";
  const headlineHl = cms.headline_highlight || "revenue";
  const headlinePost = cms.headline_post || "with the Official WhatsApp Business API";
  const eyebrow = cms.eyebrow || "Official WhatsApp Business API · India";
  const subhead = cms.subhead || "Automate customer conversations, sales, support and engagement using WhatsApp Business API, AI Chatbots, Shared Team Inbox and Multi-Agent Collaboration.";
  const microcopy = cms.microcopy || ["No credit card required", "Setup in 10 minutes", "Meta-compliant"];
  const supportingLine = cms.supporting_line || "Trusted by 900+ businesses across India to automate sales, support and customer engagement on WhatsApp.";

  const trustBadges = (cms.trust_badges || []).filter((b) => b?.enabled !== false)
    .slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const trustBadgesSecondary = (cms.trust_badges_secondary || []).filter((b) => b?.enabled !== false)
    .slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const primaryCta = cms.primary_cta || { enabled: true, label: "Book a free demo", href: "" };
  const whatsappCta = cms.whatsapp_cta || { enabled: true, label: "WhatsApp Now", href: "" };
  const watchDemoCta = cms.watch_demo_cta || { enabled: true, label: "Watch 2-min demo" };
  const onboardingCta = cms.onboarding_cta || { enabled: true, label: "Complete WhatsApp Onboarding", href: "/whatsapp-onboarding" };

  const watchDemo = cms.watch_demo || { enabled: true, youtube_url: "", embed_url: "", autoplay: true };
  const chatDemo = cms.chat_demo || null;
  const floatingStats = (cms.floating_stats || []).filter((s) => s?.enabled !== false);

  const [watchOpen, setWatchOpen] = useState(false);
  const handlePrimary = () => {
    if (primaryCta.href) window.open(primaryCta.href, "_blank", "noopener,noreferrer");
    else onBookDemo?.();
  };
  const handleWatch = () => {
    if (watchDemo?.enabled) setWatchOpen(true);
    else onBookDemo?.();
  };

  return (
    <section id="top" className="relative pt-28 sm:pt-32 pb-20 overflow-hidden">
      <div className="absolute inset-0 hero-grid opacity-60" />
      <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-emerald-200/40 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 h-[480px] w-[480px] rounded-full bg-blue-200/40 blur-3xl" />

      <motion.div
        variants={stagger}
        initial="hidden"
        animate="show"
        className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center"
      >
        <div className="lg:col-span-7">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 backdrop-blur px-3 py-1.5 text-xs font-semibold text-slate-700">
            <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
            {eyebrow}
          </motion.div>

          <motion.h1
            variants={fadeUp}
            data-testid="hero-headline"
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {headlinePre}{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-blue-600 via-cyan-500 to-emerald-500 bg-clip-text text-transparent">
                {headlineHl}
              </span>
              <span className="absolute -bottom-1 left-0 right-0 h-2 rounded-full bg-emerald-200/60" />
            </span>{" "}
            {headlinePost}
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-6 max-w-xl text-lg text-slate-600 leading-relaxed">
            {subhead}
          </motion.p>

          {trustBadges.length > 0 && (
            <motion.div variants={fadeUp} className="mt-7 flex flex-wrap gap-2" data-testid="hero-trust-badges">
              {trustBadges.map((b) => (
                <span
                  key={b.id || b.label}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm"
                >
                  <Icon name={b.icon} className="h-3.5 w-3.5 text-emerald-600" />
                  {b.label}
                </span>
              ))}
            </motion.div>
          )}

          {trustBadgesSecondary.length > 0 && (
            <motion.div variants={fadeUp} className="mt-2 flex flex-wrap gap-2" data-testid="hero-trust-badges-secondary">
              {trustBadgesSecondary.map((b) => (
                <span
                  key={b.id || b.label}
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50/70 border border-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-800"
                >
                  <Icon name={b.icon} className="h-3.5 w-3.5 text-emerald-600" />
                  {b.label}
                </span>
              ))}
            </motion.div>
          )}

          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
            {primaryCta.enabled !== false && (
              <Button
                onClick={handlePrimary}
                data-testid="hero-primary-cta"
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-6 h-12 text-base font-semibold shadow-[0_10px_30px_-12px_rgba(16,185,129,0.6)] hover:-translate-y-0.5 transition-all"
              >
                {primaryCta.label || "Book a free demo"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
            {whatsappCta.enabled !== false && whatsappCta.href && (
              <a
                href={whatsappCta.href}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp-cta"
                className="inline-flex items-center justify-center rounded-full h-12 px-6 text-base font-semibold bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-50 transition-all"
              >
                <Icon name="MessageCircle" className="mr-2 h-4 w-4" />
                {whatsappCta.label || "WhatsApp Now"}
              </a>
            )}
            {watchDemoCta.enabled !== false && (
              <Button
                variant="outline"
                onClick={handleWatch}
                data-testid="hero-secondary-cta"
                className="rounded-full h-12 px-5 text-sm font-semibold border-slate-300 hover:bg-slate-50 text-slate-700"
              >
                <Play className="mr-2 h-4 w-4 text-blue-600" /> {watchDemoCta.label || "Watch 2-min demo"}
              </Button>
            )}
          </motion.div>

          {onboardingCta.enabled !== false && onboardingCta.href && (
            <motion.div variants={fadeUp} className="mt-3">
              <a
                href={onboardingCta.href}
                target={onboardingCta.href?.startsWith("http") ? "_blank" : undefined}
                rel={onboardingCta.href?.startsWith("http") ? "noopener noreferrer" : undefined}
                data-testid="hero-onboarding-cta"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 underline-offset-4 hover:underline transition-colors"
              >
                <Icon name="ShieldCheck" className="h-4 w-4 text-emerald-600" />
                {onboardingCta.label || "Complete WhatsApp Onboarding"}
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </motion.div>
          )}

          {microcopy.length > 0 && (
            <motion.div variants={fadeUp} className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-slate-500" data-testid="hero-microcopy">
              {microcopy.map((m) => (
                <span key={m} className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" /> {m}</span>
              ))}
            </motion.div>
          )}

          {supportingLine && (
            <motion.div variants={fadeUp} className="mt-6 flex items-center gap-2 text-sm text-slate-600" data-testid="hero-supporting-line">
              <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span>{supportingLine}</span>
            </motion.div>
          )}
        </div>

        <motion.div variants={fadeUp} className="lg:col-span-5 relative flex justify-center lg:justify-end">
          <div className="relative">
            {chatDemo?.enabled !== false && <ChatDemo config={chatDemo} />}
            {floatingStats.map((s) => (
              <FloatingStat key={s.id || s.label} {...s} />
            ))}
          </div>
        </motion.div>
      </motion.div>

      <WatchDemoModal
        open={watchOpen}
        onClose={() => setWatchOpen(false)}
        embedUrl={watchDemo.embed_url}
        youtubeUrl={watchDemo.youtube_url}
        autoplay={watchDemo.autoplay !== false}
        title={watchDemoCta.label || "Product demo"}
      />
    </section>
  );
};
